import React, { useState, useCallback } from "react";
import { useAccountToDisplaySelector } from "../../../redux/actions/user";
import { useDateRangeSelector, setLoading } from "../../../redux/actions/app";
import { useDispatch } from "react-redux";
import Axios from "axios";
import CONFIG from "../../../model/TSConfig";
import { tt } from "../../../utils/i18n/i18n";
import moment from "moment-timezone";
import { PaginatedTable } from "./PaginatedTable";

const PAGE_SIZE = 20;

interface JetpackWinner {
  id: string;
  ts: number;
  reference: string;
  name: string;
  email: string;
  prize: string;
  redeemed: boolean;
  rTs?: number;
}

export const WinnersTable = () => {
  const dispatch = useDispatch();
  const { accountToDisplay } = useAccountToDisplaySelector();
  const dateRange = useDateRangeSelector();
  const [totalCount, setTotalCount] = useState(0);
  const [winners, setWinners] = useState<JetpackWinner[]>([]);

  const fetchCount = useCallback(async () => {
    if (!accountToDisplay?.trackingID || !dateRange) {
      return;
    }

    try {
      const response = await Axios({
        method: "get",
        url: `${CONFIG.jetpackBackendUrl}/custdash/winners/count`,
        params: {
          owner: accountToDisplay?.trackingID,
          sts: moment(dateRange.start).unix(),
          ets: moment(dateRange.end).unix()
        }
      });

      if (response.data?.payload) {
        setTotalCount(response.data.payload);
      }
    } catch (error) {
      console.error("Error fetching winners count:", error);
      setTotalCount(0);
    }
  }, [accountToDisplay?.trackingID, dateRange]);

  const fetchWinners = useCallback(
    async (pageIndex: number) => {
      if (!accountToDisplay?.trackingID || !dateRange) {
        return;
      }

      dispatch(setLoading(true));

      try {
        const response = await Axios({
          method: "get",
          url: `${CONFIG.jetpackBackendUrl}/custdash/winners`,
          params: {
            owner: accountToDisplay?.trackingID,
            sts: moment(dateRange.start).unix(),
            ets: moment(dateRange.end).unix(),
            pageIndex,
            pageSize: PAGE_SIZE
          }
        });

        if (response.data?.payload) {
          setWinners(response.data.payload);
        }
      } catch (error) {
        console.error("Error fetching winners:", error);
        setWinners([]);
      } finally {
        dispatch(setLoading(false));
      }
    },
    [accountToDisplay, dateRange, dispatch]
  );

  const renderTableHeaders = useCallback(() => {
    return (
      <tr>
        <th>{tt("date_and_hour")}</th>
        <th>{tt("reference")}</th>
        <th>{tt("name")}</th>
        <th>{tt("email")}</th>
        <th>{tt("prize")}</th>
        <th>{tt("redeemed")}</th>
        <th>{tt("redemption_date")}</th>
      </tr>
    );
  }, []);

  const renderTableRow = useCallback((winner: JetpackWinner, index: number) => {
    return (
      <tr key={`winner-${index}`}>
        <td>{moment.unix(winner.ts).format(tt("date_format_full_date"))}</td>
        <td>{winner.reference}</td>
        <td>{winner.name}</td>
        <td>{winner.email}</td>
        <td>{winner.prize}</td>
        <td>{winner.redeemed ? tt("yes") : tt("no")}</td>
        <td>
          {winner.rTs
            ? moment.unix(winner.rTs).format(tt("date_format_full_date"))
            : "-"}
        </td>
      </tr>
    );
  }, []);

  if (!accountToDisplay?.trackingID) {
    return null;
  }

  return (
    <div className="winners-container">
      <PaginatedTable<JetpackWinner>
        pageSize={PAGE_SIZE}
        totalCount={totalCount}
        data={winners}
        onFetchCount={fetchCount}
        onFetchData={fetchWinners}
        renderHeaders={renderTableHeaders}
        renderRow={renderTableRow}
        keyPrefix="winners"
        showDownloadButton={false}
        trackingID={accountToDisplay.trackingID}
      />
    </div>
  );
};

export default WinnersTable;
